/**
 * Helper functions for working with the metrics
 */

import { Interpretations } from '../interfaces/analysisResponse';
import { ScoreDelta } from '../interfaces/postReply.interface';
import {
  DetailedMetrics,
  SentimentMetrics,
  ToxicityMetrics,
} from '../interfaces/postScore.interface';

/**
 * Get a human-readable sentiment label
 */
export function getSentimentLabel(sentiment: SentimentMetrics): string {
  const primary = sentiment.primary.toLowerCase();
  const score =
    sentiment.scores[primary.toUpperCase() as keyof typeof sentiment.scores];
  const confidence = sentiment.confidence;

  if (confidence < 0.6) return 'Mixed';
  if (primary === 'positive') return 'Positive';
  if (primary === 'negative') return 'Negative';
  return 'Neutral';
}

/**
 * Get a color for a given score (0-100)
 */
export function getScoreColor(score: number): string {
  if (score >= 80) return '#10b981'; // Green
  if (score >= 60) return '#3b82f6'; // Blue
  if (score >= 40) return '#f59e0b'; // Yellow
  return '#ef4444'; // Red
}

/** Map a 0-100 score to a trilirivos accent CSS-variable expression. */
export function getScoreColorVar(score: number): string {
  if (score >= 80) return 'var(--accent-emeraude)';
  if (score >= 60) return 'var(--accent-saphir)';
  if (score >= 40) return 'var(--accent-flamme)';
  return 'var(--color-danger)';
}

/** Map a 0-100 score to a short human label. Matches the canonical labels
 * used in TextAnalysisResponse/helpers/getScoreLabel.ts — keep in sync. */
export function getScoreLabel(score: number): string {
  if (score >= 80) return 'Excellent';
  if (score >= 60) return 'Good';
  if (score >= 40) return 'Needs Improvement';
  return 'Requires Revision';
}

/**
 * Get a human-readable toxicity level
 */
export function getToxicityLevel(toxicity: ToxicityMetrics): string {
  if (toxicity.is_toxic) return 'High';
  if (toxicity.max_score > 0.4) return 'Moderate';
  if (toxicity.max_score > 0.2) return 'Low';
  return 'Minimal';
}

/**
 * Format a score change for display.
 *
 * Returns the empty string for a zero delta so naive callers concatenating
 * `${score.toFixed(1)}${formatScoreChange(delta)}` don't render `74.60.0`
 * from a no-op rescore (TRIL-84 — the same bug surfaced in the feed-card
 * render and was fixed in PR #46 by gating the JSX span; this helper is
 * unused today but exported, so applying the same gate at the source
 * prevents future callers from re-introducing the bug).
 *
 * Caller is responsible for any separating whitespace between the score
 * and the (signed) delta.
 */
export function formatScoreChange(delta: ScoreDelta): string {
  if (delta.change === 0) return '';
  const sign = delta.change > 0 ? '+' : '';
  return `${sign}${delta.change.toFixed(1)}`;
}

/**
 * Get an emoji for the score direction
 */
export function getScoreDirectionEmoji(
  direction: ScoreDelta['change_direction'],
): string {
  if (direction === 'up') return '📈';
  if (direction === 'down') return '📉';
  return '➡️';
}

/**
 * Check if any flags should block publishing
 */
export function hasBlockingFlags(interpretations: Interpretations): boolean {
  return interpretations.flags.some(
    (flag) =>
      flag.includes('toxic') ||
      flag.includes('spam') ||
      flag.toLowerCase().includes('block'),
  );
}

/**
 * Calculate a composite quality score from multiple metrics
 */
export function getCompositeQualityScore(metrics: DetailedMetrics): number {
  const weights = {
    readability: 0.25,
    grammar: 0.25,
    credibility: 0.25,
    sentiment: 0.25,
  };

  const scores = {
    readability: metrics.quality.readability_score,
    grammar: metrics.quality.grammar_score,
    credibility: metrics.credibility.score * 100,
    sentiment: (1 - metrics.toxicity.max_score) * 100,
  };

  return Object.entries(weights).reduce((total, [key, weight]) => {
    return total + scores[key as keyof typeof scores] * weight;
  }, 0);
}

export interface SentimentNarrativeLike {
  sentiment_summary?: string | null;
  confidence_explanation?: string | null;
  contribution_explanation?: string | null;
}
export interface AnalysisVersionLike {
  version?: number | null;
  storyAnalyzer?: unknown;
  sentiment?: SentimentNarrativeLike | null;
}
export interface StoryAnalysisLike {
  analysis?: Array<AnalysisVersionLike | null> | null;
}

/**
 * Returns the storyAnalyzer of the highest-version analysis **that has
 * analysis data**. A newer in-progress version with no storyAnalyzer is
 * skipped in favor of the latest version that does have data; returns null
 * only when no version has data.
 */
export function selectLatestAnalysis<T = Record<string, unknown>>(
  storyAnalysis: StoryAnalysisLike | null | undefined,
): T | null {
  const versions = storyAnalysis?.analysis?.filter(
    (a): a is AnalysisVersionLike => !!a && a.storyAnalyzer != null,
  );
  if (!versions || versions.length === 0) return null;
  const latest = [...versions].sort(
    (a, b) => (b.version ?? 0) - (a.version ?? 0),
  )[0];
  return (latest.storyAnalyzer ?? null) as T | null;
}

/**
 * Returns the `sentiment` narrative (SentimentResponse) of the same
 * highest-version analysis that {@link selectLatestAnalysis} settles on, so the
 * sentiment bar and its explanatory narrative are always read from one entry.
 * Returns null when that version carries no sentiment narrative.
 */
export function selectLatestSentimentNarrative(
  storyAnalysis: StoryAnalysisLike | null | undefined,
): SentimentNarrativeLike | null {
  const versions = storyAnalysis?.analysis?.filter(
    (a): a is AnalysisVersionLike => !!a && a.storyAnalyzer != null,
  );
  if (!versions || versions.length === 0) return null;
  const latest = [...versions].sort(
    (a, b) => (b.version ?? 0) - (a.version ?? 0),
  )[0];
  return latest.sentiment ?? null;
}
